import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/ui/confirm";
import { DocPartyStrip } from "@/modules/docs/DocPartyStrip";
import { RelatedDocLink } from "@/modules/docs/RelatedDocLink";
import { DocSlip, printDocSlip } from "@/modules/docs/print/DocSlip";
import { buildGrPrint } from "@/modules/docs/print/doc-print-math";
import { useOrgLetterhead } from "@/modules/docs/print/use-org-letterhead";
import { LifecycleStatus } from "@/modules/core/components/LifecycleStatus";
import { AdvancedSection } from "@/modules/shell/AdvancedSection";
import { FieldGroup } from "@/modules/shell/FieldGroup";
import { DataTable } from "@/modules/shell/data-table";
import { DeskDocBackLink } from "@/modules/shell/DeskDocBackLink";
import { DeskDocTabs } from "@/modules/shell/DeskDocTabs";
import { DocActivity } from "@/modules/shell/DocActivity";
import { EmptyState } from "@/modules/shell/EmptyState";
import { PageFrame } from "@/modules/shell/PageFrame";
import { PageHeader } from "@/modules/shell/PageHeader";
import { RemindMeButton } from "@/modules/shell/RemindMeButton";
import { useDeskDocTab } from "@/modules/shell/use-desk-doc-tab";
import { useRecordRecentDoc } from "@/modules/shell/use-record-recent-doc";
import { TagsAssigneesPanel } from "@/modules/tags/components/TagsAssigneesPanel";
import { getGoodsReceipt, voidGoodsReceipt } from "@/shared/api/purchasing";
import { goodsReceiptLineColumns } from "./po-detail-columns";

const GR_DOC_TABS = ["receipt", "lines"] as const;

export function GoodsReceiptDetailPage() {
  const { companySlug, receiptId } = useParams();
  const navigate = useNavigate();
  const confirmUi = useConfirm();
  const { letterhead } = useOrgLetterhead(companySlug);
  const [data, setData] = useState<Awaited<
    ReturnType<typeof getGoodsReceipt>
  > | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [docTab, setDocTab] = useDeskDocTab("receipt", GR_DOC_TABS);

  async function reload() {
    if (!companySlug || !receiptId) return;
    const d = await getGoodsReceipt(companySlug, receiptId);
    setData(d);
  }

  useEffect(() => {
    let cancelled = false;
    if (!companySlug || !receiptId) return;
    getGoodsReceipt(companySlug, receiptId)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : "Failed";
        if (msg === "Not signed in") navigate("/login", { replace: true });
        else setError(msg);
      });
    return () => {
      cancelled = true;
    };
  }, [companySlug, receiptId, navigate]);

  useRecordRecentDoc({
    companySlug,
    kind: "goodsReceipt",
    id: data?.receipt.id,
    label: data?.receipt.number,
    path:
      companySlug && data?.receipt.id
        ? `/c/${companySlug}/purchasing/receipts/${data.receipt.id}`
        : undefined,
    hint: data?.receipt.supplierName,
  });

  async function onVoid() {
    if (!companySlug || !receiptId) return;
    const ok = await confirmUi({
      title: "Void this goods receipt?",
      description:
        "Stock and AP will reverse. The number is kept (gap OK).",
      confirmLabel: "Void",
      destructive: true,
    });
    if (!ok) return;
    setBusy(true);
    setError(null);
    /** One key for soft-confirm retry — plan/24 */
    const idempotencyKey = `gr-void-${crypto.randomUUID()}`;
    try {
      await voidGoodsReceipt(companySlug, receiptId, { idempotencyKey });
      await reload();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Void failed");
    } finally {
      setBusy(false);
    }
  }

  if (!companySlug || !receiptId) return null;
  if (!data) {
    return (
      <PageFrame width="narrow" className="py-4">
        {error ? (
          <Alert variant="destructive">{error}</Alert>
        ) : (
          <Alert variant="muted">Loading goods receipt…</Alert>
        )}
      </PageFrame>
    );
  }

  const { receipt: gr, lines } = data;
  const printData = buildGrPrint({
    number: gr.number,
    poNumber: gr.poNumber,
    lifecycle: gr.lifecycle,
    warehouseName: gr.warehouseName,
    warehouseCode: gr.warehouseCode,
    supplierInvoiceRef: gr.supplierInvoiceRef,
    issuedAt: gr.postedAt,
    lines: lines.map((l) => ({
      productName: l.productName,
      qty: l.qty,
      unitCost: l.unitCost,
    })),
  });

  return (
    <PageFrame width="narrow">
      <div className="pos-receipt-print" aria-hidden>
        <DocSlip data={printData} letterhead={letterhead} />
      </div>
      <div className="no-print">
        <div className="mb-3">
          <DeskDocBackLink
            listBasePath={`/c/${companySlug}/purchasing/orders/${gr.purchaseOrderId}`}
            listLabel={gr.poNumber ?? "Purchase order"}
          />
        </div>
        <PageHeader
          eyebrow="Goods receipt"
          title={gr.number}
          description={[
            gr.supplierName,
            gr.warehouseCode || gr.warehouseName,
          ]
            .filter(Boolean)
            .join(" · ")}
          actions={
            <div className="flex flex-wrap gap-2">
              <RemindMeButton
                companySlug={companySlug}
                kind="goods_receipt"
                docId={receiptId}
                docNumber={gr.number}
                partyName={gr.supplierName}
                disabled={busy}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => printDocSlip()}
              >
                Print
              </Button>
              {gr.lifecycle === "posted" && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={() => void onVoid()}
                >
                  Void receipt
                </Button>
              )}
            </div>
          }
        />
        <div className="mt-2">
          <LifecycleStatus value={gr.lifecycle} showLabel />
        </div>
        <DocPartyStrip
          companySlug={companySlug}
          partyId={gr.supplierId}
          partyName={gr.supplierName}
          roleLabel="Supplier"
          primaryPhone={gr.supplierPrimaryPhone}
          primaryEmail={gr.supplierPrimaryEmail}
          primaryAddress={gr.supplierPrimaryAddress}
          partyNotes={gr.supplierNotes}
          docNotes={gr.notes}
        />
        <TagsAssigneesPanel
          companySlug={companySlug}
          docType="goods_receipt"
          docId={receiptId}
          linkPath={`/c/${companySlug}/purchasing/receipts/${receiptId}`}
          docLabel={[gr.number, gr.supplierName].filter(Boolean).join(" · ")}
        />
        <DeskDocTabs
          aria-label="Goods receipt views"
          value={docTab}
          onChange={setDocTab}
          items={[
            { id: "receipt", label: "Receipt" },
            { id: "lines", label: "Lines", count: lines.length },
          ]}
        />
      </div>

      {docTab === "receipt" && (
        <>
          <FieldGroup title="Receiving" className="mt-6" level="sub">
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
              <dt className="text-[var(--color-muted)]">Purchase order</dt>
              <dd className="m-0">
                <RelatedDocLink
                  companySlug={companySlug}
                  kind="purchase_order"
                  docId={gr.purchaseOrderId}
                >
                  {gr.poNumber ?? "Purchase order"}
                </RelatedDocLink>
              </dd>
              <dt className="text-[var(--color-muted)]">Warehouse</dt>
              <dd className="m-0">
                {gr.warehouseName}
                {gr.warehouseCode ? (
                  <span className="font-mono text-xs text-[var(--color-muted)]">
                    {" "}
                    · {gr.warehouseCode}
                  </span>
                ) : null}
              </dd>
              {gr.postedAt ? (
                <>
                  <dt className="text-[var(--color-muted)]">Posted</dt>
                  <dd className="m-0 text-xs">{gr.postedAt}</dd>
                </>
              ) : null}
              {gr.purchaseShipmentId && gr.shipmentNumber ? (
                <>
                  <dt className="text-[var(--color-muted)]">In transit</dt>
                  <dd className="m-0">
                    <Link
                      to={`/c/${companySlug}/purchasing/in-transit/${gr.purchaseShipmentId}`}
                      className="font-mono text-sm no-underline hover:underline"
                    >
                      {gr.shipmentNumber}
                    </Link>
                  </dd>
                </>
              ) : null}
            </dl>
          </FieldGroup>

          <AdvancedSection className="no-print" subtitle="refs">
            <FieldGroup title="Refs" level="sub">
              <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
                {gr.supplierInvoiceRef ? (
                  <>
                    <dt className="text-[var(--color-muted)]">
                      Supplier invoice #
                    </dt>
                    <dd className="m-0">{gr.supplierInvoiceRef}</dd>
                  </>
                ) : (
                  <dd className="m-0 text-[var(--color-muted)]">None set</dd>
                )}
              </dl>
            </FieldGroup>
          </AdvancedSection>
        </>
      )}

      {docTab === "lines" && (
        <DataTable
          listId="goods-receipt-lines"
          companySlug={companySlug}
          className="mt-2"
          columns={goodsReceiptLineColumns()}
          data={lines}
          getRowId={(r) => r.id}
          manualSorting={false}
          empty={
            <EmptyState
              title="No lines"
              description="This receipt has no product lines."
            />
          }
        />
      )}

      {error && (
        <Alert variant="destructive" className="mt-4">
          {error}
        </Alert>
      )}

      <DocActivity
        companySlug={companySlug}
        docType="goods_receipt"
        docId={receiptId}
        linkPath={`/c/${companySlug}/purchasing/receipts/${receiptId}`}
      />
    </PageFrame>
  );
}
